import React from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
} from '@mui/material';

interface EditNameDialogProps {
  open: boolean;
  label: string;
  initialValue: string;
  saving?: boolean;
  error?: string | null;
  onClose: () => void;
  onSave: (value: string) => Promise<void> | void;
}

export const EditNameDialog: React.FC<EditNameDialogProps> = ({
  open,
  label,
  initialValue,
  saving = false,
  error = null,
  onClose,
  onSave,
}) => {
  const [value, setValue] = React.useState(initialValue);

  React.useEffect(() => {
    if (open) {
      setValue(initialValue);
    }
  }, [initialValue, open]);

  const normalizedValue = value.trim();
  const canSave = normalizedValue.length > 0 && normalizedValue.length <= 50 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    await onSave(normalizedValue);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          autoFocus
          fullWidth
          label={label}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          disabled={saving}
          inputProps={{ maxLength: 50 }}
          helperText={`${normalizedValue.length}/50`}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {saving ? 'Saving...' : 'Save Name'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};